/** Subscription status helpers from auth user payload. */

import { needsSubscriptionGate } from './rolePaths';

export function getSubscription(user) {
  return user?.subscription ?? { status: 'none' };
}

export function isSubscriptionActive(user) {
  const sub = getSubscription(user);
  return ['active', 'trial'].includes(sub.status);
}

export function subscriptionDaysRemaining(user) {
  const sub = getSubscription(user);
  return sub.days_remaining ?? 0;
}

export function isSubscriptionExpiringSoon(user, withinDays = 7) {
  if (!isSubscriptionActive(user)) return false;
  const days = subscriptionDaysRemaining(user);
  return days >= 0 && days <= withinDays;
}

export function isSubscriptionExpired(user) {
  const sub = getSubscription(user);
  return sub.status === 'expired' || (isSubscriptionActive(user) === false && sub.status !== 'none' && sub.status !== 'pending');
}

export function isSubscriptionModuleEnabled(user, moduleKey) {
  const sub = getSubscription(user);
  if (!isSubscriptionActive(user)) return false;
  const modules = sub.features?.modules ?? {};
  if (Object.prototype.hasOwnProperty.call(modules, moduleKey)) {
    return Boolean(modules[moduleKey]);
  }
  return true;
}

const UPGRADE_PLAN_BANNER_MESSAGE = 'Upgrade your plan for better experience.';

export function isOnFreePlan(user) {
  const sub = getSubscription(user);
  return sub.status === 'active' && sub.plan_slug === 'free';
}

export function subscriptionGateBannerMessage(user) {
  if (!needsSubscriptionGate(user)) {
    return null;
  }
  return UPGRADE_PLAN_BANNER_MESSAGE;
}

export function subscriptionBannerMessage(user) {
  const gateMessage = subscriptionGateBannerMessage(user);
  if (gateMessage) {
    return gateMessage;
  }

  const sub = getSubscription(user);
  if (sub.status === 'none' || isOnFreePlan(user)) {
    return UPGRADE_PLAN_BANNER_MESSAGE;
  }
  if (sub.status === 'expired') {
    return 'Your subscription has expired. Renew to restore premium features.';
  }
  const days = subscriptionDaysRemaining(user);
  if (days <= 0 && sub.status !== 'none') {
    return 'Your subscription expires today. Renew now to avoid interruption.';
  }
  if (days > 0 && days <= 7) {
    return `Your subscription expires in ${days} day(s). Consider renewing early.`;
  }
  if ((sub.limits_reached ?? []).length > 0) {
    return 'You have reached plan limits. Upgrade to add more users or resources.';
  }
  return null;
}
